import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import {
  useAttachPaymentMutation,
  useCustomerCallTopUpsMutation,
} from "../../../store/payments/paymentsApi";
import { useState } from "react";
import {
  IPaymentForm,
} from "../../../interfaces/payments/IPaymentsProps";
import Box from "../../../shared/box/Box";
import Input from "../../../shared/input";
import Button from "../../../shared/button";
import { useForm } from "react-hook-form";

const PaymentForm = () => {
  const [callTopUps, { isLoading: isTopUpLoading }] =
    useCustomerCallTopUpsMutation();
  const [attachPayment, { isLoading: isAttachLoading }] =
    useAttachPaymentMutation();
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string>("");
  const [paymentSuccess, setPaymentSuccess] = useState<boolean>();

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<IPaymentForm>({
    defaultValues: {
      amount: 5,
    },
  });

  const onTopUpSubmit = async (
    amount: number,
    customerId: string,
    paymentMethodId: string
  ) => {
    try {
      const res = await callTopUps({
        amount,
        currency: "USD",
        customerId,
        paymentMethodId,
      }).unwrap();

      console.log({ res });

      if (!stripe) return;
      const { error } = await stripe.confirmCardPayment(res.data.clientSecret);
      if (error) {
        setPaymentError(`Payment failed: ${error.message}`);
      } else {
        setPaymentSuccess(true);
      }
    } catch (e) {
      console.error({ e });
    }

    //
  };

  const onPaymentSubmit = async (
    data: IPaymentForm
  ) => {
    if (!stripe || !elements) return;

    setIsProcessing(true);

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      card: cardElement as any,
      type: "card",
      billing_details: {
        name: data.userName,
        email: data.userEmail,
      },
    });

    if (error) {
      setPaymentError(error?.message || "");
      setIsProcessing(false);
    } else {
      if (paymentMethod.id) {
        console.log("Payment Method:", paymentMethod, "Payment successful!");

        try {
          const res = await attachPayment({
            ...data,
            paymentMethodId: String(paymentMethod.id),
          }).unwrap();
          console.log({ attached: res });
          if (res) {
            onTopUpSubmit(
              +data.amount,
              res.data.customerId,
              res.data.paymentMethodId
            );
          }
        } catch (e) {
          console.error(e);
          const _e = e as { message: string };
          setPaymentError(String(_e?.message) || "Cannot attach the payment");
        }
      }
      setIsProcessing(false);
    }
  };

  const handleTopUp = () => setPaymentSuccess(false);
  return paymentSuccess ? (
    <>
      <Box title="">
        <>
          <p>Your payment has been completed </p>
          <div className="w-full my-2 text-center">
            <Button onClickButton={handleTopUp} label="Make another top up" />
          </div>
        </>
      </Box>
    </>
  ) : (
    <form onSubmit={handleSubmit(onPaymentSubmit)}>
      <div className="w-full md:w-6/12 mx-auto">
        <Box title="Credit or debit card">
          <>
            <CardElement />
            <input
              type="range"
              id="range"
              min={5}
              step={5}
              max={10000}
              {...register("amount")}
              className={"border border-borderColor rounded w-full px-3 py-2"}
            />
            <div className="my-3 font-semibold text-center">
              {new Intl.NumberFormat("de-DE", {
                style: "currency",
                currency: "USD",
              }).format(watch("amount"))}
            </div>
          </>
        </Box>
      </div>
      <div className="w-full">
        <Box title="Billing Address">
          <>
            <div className="flex flex-wrap -mx-2">
              <Input
                register={register("userName", {
                  required: "User name is required",
                })}
                label="Full Name"
                containerStyle="w-full px-2"
                error={errors.userName?.message}
              />
              <Input
                register={register("userEmail", {
                  required: "User email is required",
                })}
                label="Email"
                containerStyle="w-full px-2"
                error={errors.userEmail?.message}
              />
              <Input
                register={register("billingAddress.address1", {
                  required: "Address Line 1 is required",
                })}
                label="Address Line 1"
                containerStyle="w-full px-2"
              />
              <Input
                register={register("billingAddress.address2")}
                label="Address Line 2"
                containerStyle="w-full px-2"
              />
              <Input
                register={register("billingAddress.city", {
                  required: "City is required",
                })}
                label="City"
                containerStyle="w-full md:w-8/12 px-2"
              />
              <Input
                register={register("billingAddress.postCode", {
                  required: "Postcode is required",
                })}
                label="Postcode/Zipcode"
                containerStyle="w-full md:w-4/12 px-2"
              />
              <Input
                register={register("billingAddress.country", {
                  required: "Country is required",
                })}
                label="Country"
                containerStyle="w-full px-2"
              />
            </div>

            {paymentError && (
              <div className="error text-red-500 text-xs">{paymentError}</div>
            )}
            <div className="text-center">
              <Button
                type="submit"
                disabled={
                  isProcessing || isTopUpLoading || isAttachLoading || !stripe
                }
                loading={isProcessing || isTopUpLoading || isAttachLoading}
                className=" bg-saloon-primary w-full py-2 rounded-2xl font-semibold hover:bg-opacity-80"
                label={isProcessing ? "Processing..." : "Save & Pay"}
              />
            </div>
          </>
        </Box>
      </div>
    </form>
  );
};

export default PaymentForm;
